import { addStatus } from '../../shared/status/statusActions';
import {
  checkForInvite,
  createEmailUser,
  createInvite,
  getMemberships,
  getUserProfile,
  handleInvitedSignup,
  handleOpenSignup,
  listenForAuthChanges,
  loginWithEmailAndPassword,
  loginWithGoogle,
  logout,
  resetPassword,
  updateAuthProfile,
  updateUserProfile,
} from './authService';
import { useAuthStore } from './authStore';
import { AuthUser, UserRole } from './types';
import { useOrganizationStore } from '../../shared/organization/organizationStore';
import { navigate } from '../../utils/navigation';
import { getOrganization } from '../../shared/organization/organizationService';
import { functionsSendInviteEmail } from '../../shared/services/firestoreUtilities';
import { updateOrgMembersActions } from '../../shared/organization/organizationActions';
import { useOrgUserStore } from '../../shared/organization/orgUserStore';
import { useOrgUserInviteStore } from '@shared/organization/orgUserInviteStore';

export const handleLoginWithGoogle = async (): Promise<void> => {
  const store = useAuthStore.getState();
  const orgStore = useOrganizationStore.getState();
  try {
    const auth = await loginWithGoogle();

    if (!auth || !auth.email) {
      throw new Error('Google Sign-In failed');
    }

    const profile = await getUserProfile(auth.uid);
    if (profile) {
      const memberships = await getMemberships(profile.uid);
      const org = await getOrganization(memberships[0].organizationId);
      orgStore.setOrganization(org);
      store.setAuth({ ...profile, memberships });
      navigate('/v1/dashboard');
      return;
    }
    const invite = await checkForInvite(auth.email);
    if (invite) {
      const data = await handleInvitedSignup(auth, invite);
      store.setAuth(data);
      navigate('/v1/create-profile');
      return;
    }

    const { userProfile, orgProfile, memberships } =
      await handleOpenSignup(auth);

    store.setAuth({ ...userProfile, memberships });
    orgStore.setOrganization({
      ...orgProfile,
      subscriptionStatus: orgProfile.subscriptionStatus,
    });
    navigate('/v1/create-profile');
    return;

    // store.setAuth(auth);
  } catch (error) {
    console.error('Google Sign-In failed:', error);
    addStatus({
      type: 'error',
      message: 'Google Sign-In failed',
      autoDismiss: true,
    });
  }
};

export const userCan = (requiredRole: string): Promise<boolean> => {
  const auth = useAuthStore.getState().auth;
  const org = useOrganizationStore.getState().organization;

  if (!auth) {
    addStatus({
      type: 'error',
      message: 'Not authenticated.',
      autoDismiss: true,
    });
    return Promise.resolve(false);
  }

  // To activate multiple orgs per user
  // Updated auth.memberships to key value pair
  // Add logic: IF no org > user selects org
  const membership = auth.memberships.find(
    (membership) => membership.organizationId === org?.id,
  );

  if (!membership) {
    addStatus({
      type: 'error',
      message: 'No membership for org found',
      autoDismiss: true,
    });
    return Promise.resolve(false);
  }

  return Promise.resolve(membership.role === requiredRole);
};

export const updateUserProfileAction = async (
  profile: Partial<AuthUser>,
): Promise<void> => {
  const store = useAuthStore.getState();
  const org = useOrganizationStore.getState().organization;
  const orgUserStore = useOrgUserStore.getState();
  try {
    await updateAuthProfile(profile);
    if (!store.auth) {
      addStatus({
        type: 'error',
        message: 'User not found',
        autoDismiss: true,
      });
      return;
    }
    await updateUserProfile({ uid: store.auth?.uid, ...profile });
    await updateOrgMembersActions([
      {
        type: 'update',
        uid: store.auth?.uid,
        data: { displayName: profile.displayName },
      },
    ]);
    orgUserStore.updateItem({
      id: `${store.auth?.uid}_${org?.id}`,
      displayName: profile.displayName,
    });
    store.updateAuth(profile);

    addStatus({
      type: 'success',
      message: 'Profile updated successfully',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error updating profile:', error);
    addStatus({
      type: 'error',
      message: 'Error updating profile',
      autoDismiss: true,
    });
  }
};

export const handleLoginWithEmail = async (email: string, password: string) => {
  const store = useAuthStore.getState();
  const orgStore = useOrganizationStore.getState();
  try {
    const auth = await loginWithEmailAndPassword(email, password);
    if (!auth) {
      throw new Error('Email Sign-In failed');
    }
    const org = await getOrganization(auth.memberships[0].organizationId);
    store.setAuth(auth);
    orgStore.setOrganization(org);
    navigate('/v1/dashboard');
  } catch (error) {
    addStatus({
      type: 'error',
      message: 'Email Sign-In failed',
      autoDismiss: true,
    });

    if (error instanceof Error) {
      return error.message;
    }
    return 'An unknown error occurred';
  }
};

export const handleLogout = async () => {
  const store = useAuthStore.getState();
  try {
    await logout();
    store.setAuth(null);
    navigate('/');
  } catch (error) {
    console.error('Logout failed:', error);
    addStatus({
      type: 'error',
      message: 'Logout failed. Please try again.',
      autoDismiss: true,
    });
  }
};

export const handleCreateEmailUser = async (
  email: string,
  password: string,
): Promise<string> => {
  const store = useAuthStore.getState();
  const orgStore = useOrganizationStore.getState();
  try {
    const auth = await createEmailUser(email, password);
    const invite = await checkForInvite(email);

    if (!auth) {
      throw new Error('Email Sign-Up failed');
    }

    if (!invite && auth) {
      // LAUNCH: Uncomment for launch
      const { userProfile, orgProfile, memberships } =
        await handleOpenSignup(auth);
      store.setAuth({ ...userProfile, memberships });
      orgStore.setOrganization(orgProfile);
    }
    if (invite && auth) {
      const user = {
        ...auth,
        firstName: invite.firstName,
        lastName: invite.lastName,
        displayName:
          auth.displayName ?? invite.firstName + ' ' + invite.lastName,
        role: invite.role,
      };
      const data = await handleInvitedSignup(user, invite);
      store.setAuth(data);
    }
    return 'success';
  } catch (error) {
    console.error('Error creating email user:', error);
    addStatus({
      type: 'error',
      message: 'Error creating email user',
      autoDismiss: true,
    });
    return 'error';
  }
};

export const initializeAuthListener = () => {
  const store = useAuthStore.getState();
  const orgStore = useOrganizationStore.getState();
  listenForAuthChanges(async (user: AuthUser | null | undefined) => {
    if (user) {
      try {
        const memberships = await getMemberships(user.uid);
        if (!memberships || memberships.length === 0) {
          return;
        }
        const org = await getOrganization(memberships[0]?.organizationId);
        const profile = await getUserProfile(user.uid);

        if (!profile) {
          addStatus({
            type: 'error',
            message: 'User profile not found',
            autoDismiss: true,
          });
          navigate('/v1/login');
          return;
        }

        store.setAuth({ ...profile, memberships });
        orgStore.setOrganization(org);
      } catch (error) {
        console.error('Error setting user:', error);
        addStatus({
          type: 'error',
          message: 'Error setting user',
          autoDismiss: true,
        });
      }
    } else {
      store.setAuth(null);
    }
  });
};

export const createInviteAction = async (
  firstName: string,
  lastName: string,
  email: string,
  role: UserRole,
) => {
  const organizationId = useOrganizationStore.getState().organization?.id;
  console.log('organizationId', organizationId);
  const store = useAuthStore.getState();

  if (!organizationId || !store?.auth?.uid) {
    addStatus({
      type: 'error',
      message: 'Organization not found',
      autoDismiss: true,
    });
    return;
  }

  try {
    await createInvite({
      firstName,
      lastName,
      email,
      role,
      organizationId,
      invitedBy: store.auth?.uid,
    });
    await functionsSendInviteEmail(
      email,
      `${store.auth?.firstName} ${store.auth?.lastName}`,
      useOrganizationStore.getState().organization?.name ||
        `${store.auth?.firstName} ${store.auth?.lastName}'s Organization`,
    );
    // Optimistically add invite to store
    useOrgUserInviteStore.getState().addItem({
      id: `${email}_${organizationId}`,
      email,
      firstName,
      lastName,
      displayName: `${firstName} ${lastName}`,
      invitedBy: store.auth?.uid || 'unknown',
      organizationId,
      status: 'pending',
      role: role,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    addStatus({
      type: 'success',
      message: `Invite sent to ${firstName} ${lastName}.`,
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error creating invite:', error);
    addStatus({
      type: 'error',
      message: 'Error creating invite',
      autoDismiss: true,
    });
    return error as Error;
  }
};

export const forgotPasswordAction = async (email: string) => {
  try {
    // Call your password reset function here
    await resetPassword(email);
    addStatus({
      type: 'success',
      message: 'Password reset email sent successfully.',
      autoDismiss: true,
    });
  } catch (error) {
    console.error('Error sending password reset email:', error);
    addStatus({
      type: 'error',
      message: 'Error sending password reset email',
      autoDismiss: true,
    });
    return error as Error;
  }
};

export const generateMetadata = () => {
  const timestamp = new Date().toISOString();
  const auth = useAuthStore.getState().auth;
  return {
    metadata: {
      createdBy: auth?.uid || 'unknown',
      createdAt: timestamp,
      updatedBy: auth?.uid || 'unknown',
      updatedAt: timestamp,
    },
  };
};
